// Updated CreateProjectForm.js with API integration

import React, { useState } from 'react';
import '../css/CreateProjectForm.css'
import { Link, useNavigate } from 'react-router-dom';
import { projectsAPI, authAPI, handleAPIError } from '../services/api';
import { Loader, AlertCircle, CheckCircle } from 'lucide-react';

const CreateProjectForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    budget: '',
    timeline: '',
    use_tokens: false,
    files: null,
  });

  // Check authentication on component mount
  React.useEffect(() => {
    if (!authAPI.isAuthenticated()) {
      navigate('/login');
    }
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : (type === 'file' ? files[0] : value),
    });
    
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  const getTimelineValue = (timeline) => {
    switch(timeline) {
      case '1_week': return 0;
      case '2_weeks': return 1;
      case '1_month': return 2;
      case 'flexible': return 3;
      default: return 0;
    }
  };

  const handleTimelineChange = (e) => {
    const value = parseInt(e.target.value);
    const timelineMap = {
      0: '1_week',
      1: '2_weeks',
      2: '1_month',
      3: 'flexible'
    };
    
    setFormData({
      ...formData,
      timeline: timelineMap[value]
    });
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      setError('Project title is required');
      return false;
    }
    
    if (!formData.description.trim()) {
      setError('Project description is required');
      return false;
    }
    
    if (!formData.category) {
      setError('Please select a category');
      return false;
    }
    
    if (!formData.budget) {
      setError('Please select a budget range');
      return false;
    }
    
    if (!formData.timeline) {
      setError('Please select a timeline');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Prepare data for API
      const projectData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        category: formData.category,
        budget: formData.budget,
        timeline: formData.timeline,
        use_tokens: formData.use_tokens,
        files: formData.files
      };
      
      console.log('Submitting project data:', projectData);
      
      // Call API to create project
      const response = await projectsAPI.createProject(projectData);
      
      console.log('Project created successfully:', response);
      
      setSuccess(true);
      
      // Reset form
      setFormData({
        title: '',
        description: '',
        category: '',
        budget: '',
        timeline: '',
        use_tokens: false,
        files: null,
      });
      
      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/dashboard2');
      }, 2000);
      
    } catch (error) {
      console.error('Failed to create project:', error);
      setError(handleAPIError(error));
    } finally {
      setLoading(false);
    }
  };

  // Show success message
  if (success) {
    return (
      <div className="form-container">
        <div className="success-message">
          <CheckCircle size={48} className="text-green-500" />
          <h2>Project Created Successfully!</h2>
          <p>Your project has been submitted and is now under review.</p>
          <p>Redirecting to dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="form-container">
      <h2 className="form-title">Let's Ascend with New!</h2>
      
      {error && (
        <div className="error-message">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="form">
        
        <div className="form-group">
          <label>Project Title *</label>
          <input
            type="text"
            name="title"
            placeholder="E.g. Logo Design for Clothing Brand"
            value={formData.title}
            onChange={handleChange}
            required
            disabled={loading}
          />
        </div>

        <div className="form-group">
          <label>Description *</label>
          <textarea
            name="description"
            placeholder="Brief about the project requirements..."
            value={formData.description}
            onChange={handleChange}
            required
            rows={4}
            disabled={loading}
          />
        </div>

        <div className="form-group">
          <label>Category *</label>
          <select
            name="category"
            value={formData.category}
            onChange={handleChange}
            required
            disabled={loading}
          >
            <option value="">Select a category</option>
            <option value="strategy">Strategy</option>
            <option value="graphic">Graphic Design</option>
            <option value="website">Website Creation</option>
            <option value="video">Motion & Video</option>
            <option value="social_media">Social Media</option>
            <option value="app">App Creation</option>
            <option value="sound">Sound Design</option>
            <option value="ai_ar_vr">AI, AR & VR</option>
          </select>
        </div>

        <div className="form-group">
          <label>Estimated Budget *</label>
          <select
            name="budget"
            value={formData.budget}
            onChange={handleChange}
            required
            disabled={loading}
          >
            <option value="">Select Budget Range</option>
            <option value="under_1k">Below ₹1,000</option>
            <option value="1k_5k">₹1,000 - ₹5,000</option>
            <option value="5k_10k">₹5,000 - ₹10,000</option>
            <option value="10k_50k">₹10,000 - ₹50,000</option>
            <option value="above_50k">Above ₹50,000</option>
          </select>
        </div>

        <div className="form-group">
          <label>Timeline *</label>
          <div className="slider-container">
            <input
              type="range"
              name="timeline"
              min="0"
              max="3"
              value={getTimelineValue(formData.timeline)}
              onChange={handleTimelineChange}
              className="timeline-slider"
              required
              disabled={loading}
            />
            <div className="slider-labels">
              <span className={formData.timeline === '1_week' ? 'active' : ''}>1 Week</span>
              <span className={formData.timeline === '2_weeks' ? 'active' : ''}>2 Weeks</span>
              <span className={formData.timeline === '1_month' ? 'active' : ''}>1 Month</span>
              <span className={formData.timeline === 'flexible' ? 'active' : ''}>Flexible</span>
            </div>
          </div>
        </div>

        <div className="form-group checkbox-group">
          <input
            type="checkbox"
            name="use_tokens"
            checked={formData.use_tokens}
            onChange={handleChange}
            disabled={loading}
          />
          <label>Use Creative Tokens for this project. <Link to="/creative-tokens" target='_blank' style={{ color:"red", textDecoration: "underline" }}>Learn More</Link></label>
        </div>

        <div className="form-group">
          <label>Attach File (optional)</label>
          <input
            type="file"
            name="files"
            onChange={handleChange}
            disabled={loading}
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.zip,.rar"
          />
          {formData.files && (
            <div className="file-info">
              <small>Selected: {formData.files.name}</small>
            </div>
          )}
        </div>

        <div className="form-group">
          <button 
            type="submit" 
            className="submit-button"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader className="animate-spin" size={16} />
                Creating Project...
              </>
            ) : (
              'Create Project'
            )}
          </button>
        </div>
        
        <div className="form-group">
          <Link to="/dashboard2">
            <button type="button" className="cancel-button">
              Cancel
            </button>
          </Link>
        </div>
      </form>
    </div>
  );
};

export default CreateProjectForm;